import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  z-index: 1000;
`;

const DropdownButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  justify-content: space-between;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
  }

  &:focus {
    outline: none;
    border-color: #4ecdc4;
    box-shadow: 0 0 0 3px rgba(78, 205, 196, 0.3);
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 8px 12px;
    min-width: 100px;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 6px 10px;
    min-width: 90px;
  }
`;

const ChevronIcon = styled.span`
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 8px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  min-width: 180px;
`;

const DropdownItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  color: #333;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(78, 205, 196, 0.1);
    color: #2c3e50;
  }

  ${props => props.isActive && `
    background: rgba(78, 205, 196, 0.2);
    color: #2c3e50;
    font-weight: 600;
  `}

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 0.85rem;
  }
`;

const FlagIcon = styled.span`
  font-size: 1.2rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const LanguageName = styled.span`
  flex: 1;
`;

const CheckIcon = styled.span`
  color: #4ecdc4;
  font-weight: bold;
`;

const LanguageDropdown = ({ className }) => {
  const { language, setLanguage, availableLanguages, getCurrentLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = getCurrentLanguage();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <DropdownContainer className={className} ref={dropdownRef}>
      <DropdownButton
        onClick={toggleDropdown}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        aria-label={t('selectLanguage')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <FlagIcon>{currentLang?.flag}</FlagIcon>
        <span>{currentLang?.code.toUpperCase()}</span>
        <ChevronIcon isOpen={isOpen}>▼</ChevronIcon>
      </DropdownButton>

      <AnimatePresence>
        {isOpen && (
          <DropdownMenu
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {availableLanguages.map((lang) => (
              <DropdownItem
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                isActive={lang.code === language}
                whileHover={{ x: 2 }}
                whileTap={{ scale: 0.98 }}
              >
                <FlagIcon>{lang.flag}</FlagIcon>
                <LanguageName>{lang.code.toUpperCase()}</LanguageName>
                {lang.code === language && <CheckIcon>✓</CheckIcon>}
              </DropdownItem>
            ))}
          </DropdownMenu>
        )}
      </AnimatePresence>
    </DropdownContainer>
  );
};

export default LanguageDropdown; 